import React, { useMemo, useState, useEffect, useRef, useLayoutEffect } from "react";
import "../StockInward.css";
import {
  addCustomer,
  addSalesEntry,
  addSalesPayment,
  createBillActionRequest,
  deleteSalesPayment,
  deleteSalesEntry,
  getApiBaseRoot,
  getInwardEntries,
  getItems,
  getCustomerByPhone,
  getCustomers,
  getMyBillActionRequests,
  getSalesEntries,
  saveReceiptHtml,
  updateSalesEntry,
  updateSalesEntryPaymentType,
  updateSalesPayment,
} from "../storage";
import PortalDropdown from "../components/PortalDropdown";
import { formatDisplayDate, toComparableYmd } from "../utils/dateFormat";
import { buildReceiptHtmlDocument, printReceiptWindow } from "../utils/receiptPrint";
import { getSettlementSummary, isPaymentSettlement } from "../utils/settlement";

function emptyRow() {
  return { material: "", qty: "", rate: "", amount: 0, closingStock: 0 };
}

function salesCustomerFilterLabel(entry) {
  const raw = String(entry?.customer || "").trim();
  if (!raw) return "Walk-in";
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) return "Walk-in";
  const name = lines[0] || "";
  const phone = lines[1] || "";
  return [name, phone].filter(Boolean).join(" - ") || "Walk-in";
}

const SALES_CUSTOMER_MODAL_PAGE_SIZE = 10;
const SALES_SAVED_ENTRIES_PAGE_SIZE = 10;
const SALES_PAYMENT_TYPES = ["Any", "UPI", "Cash", "Credit", "Cheque", "Acc Transfer", "Razorpay", "Card"];

function formatSalesQty(value) {
  return (Number(value) || 0).toFixed(2);
}

function formatSalesAmount(value) {
  return (Number(value) || 0).toFixed(2);
}

function normalizePhoneCandidate(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (/^[6-9]\d{9}$/.test(digits)) return `91${digits}`;
  if (/^0[6-9]\d{9}$/.test(digits)) return `91${digits.slice(1)}`;
  if (/^91[6-9]\d{9}$/.test(digits)) return digits;
  if (/^091[6-9]\d{9}$/.test(digits)) return digits.slice(1);
  if (/^0091[6-9]\d{9}$/.test(digits)) return digits.slice(2);
  if (digits.length >= 10 && digits.length <= 15) {
    return digits.startsWith("00") ? digits.slice(2) : digits;
  }
  return "";
}

function extractPhone(customerText) {
  const text = String(customerText || "");
  if (!text.trim()) return "";

  const tokenPattern = /(?:\+?\d[\d\s().-]{7,}\d)/g;
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const lineMatches = lines[i].match(tokenPattern) || [];
    for (let j = lineMatches.length - 1; j >= 0; j -= 1) {
      const normalized = normalizePhoneCandidate(lineMatches[j]);
      if (normalized) return normalized;
    }
  }

  const matches = text.match(tokenPattern) || [];
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const normalized = normalizePhoneCandidate(matches[i]);
    if (normalized) return normalized;
  }

  return "";
}

function buildWhatsAppText({ billNo, billDate, customerLines, items, totalQty, totalAmount, receiptLink }) {
  const header = `*ARF Seafoods*%0A*Bill No:* ${encodeURIComponent(billNo)}%0A*Date:* ${encodeURIComponent(
    billDate
  )}`;
  const customer = customerLines.length ? `%0A*Customer:* ${encodeURIComponent(customerLines.join(", "))}` : "";
  const itemLines = items
    .slice(0, 15)
    .map((it, idx) =>
      encodeURIComponent(
        `${idx + 1}. ${it.material} | Qty: ${formatSalesQty(it.qty)} | Rate: ${formatSalesAmount(
          it.rate
        )} | Amt: ${formatSalesAmount(it.amount)}`
      )
    )
    .join("%0A");
  const totals = `%0A*Total Qty:* ${formatSalesQty(totalQty)}%0A*Total Amt:* ${formatSalesAmount(totalAmount)}`;
  const linkText = receiptLink ? `%0A%0AView bill: ${receiptLink}` : "";
  return `${header}${customer}%0A%0A${itemLines}${itemLines ? "%0A" : ""}${totals}${linkText}`;
}

function normalizeSalesPaymentType(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "any") return "Any";
  if (raw === "upi") return "UPI";
  if (raw === "cash") return "Cash";
  if (raw === "cheque" || raw === "check") return "Cheque";
  if (raw === "acc transfer" || raw === "account transfer" || raw === "bank transfer") return "Acc Transfer";
  if (raw === "razorpay") return "Razorpay";
  if (raw === "card" || raw === "credit card" || raw === "debit card") return "Card";
  if (raw === "credit") return "Credit";
  return "Any";
}

function makeCustomerPaidForm() {
  return {
    date: new Date().toISOString().slice(0, 10),
    amount: "",
  };
}

function isEveningSalesSource(value) {
  return String(value || "").trim().toLowerCase() === "evening sales";
}

function formatSalesReceiptNo(entry) {
  if (entry?.billNo) return String(entry.billNo);
  const category = String(entry?.category || "Sale").trim().toUpperCase();
  const prefix = category === "STALL" ? "STL" : category === "MARKET" ? "MKT" : category.slice(0, 3) || "SAL";
  const tail = String(entry?.id || "").slice(-6).toUpperCase() || Date.now().toString().slice(-6);
  return `${prefix}-${tail}`;
}

export default function SalesEntry({ category: lockedCategory, currentUser }) {
  const role = (currentUser?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const canUpdatePaymentType = role === "admin" || role === "executive";
  const [category, setCategory] = useState(() => lockedCategory || "Stall");
  const [paymentType, setPaymentType] = useState("Any");
  const [saleDate, setSaleDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [customerSuggestionIndex, setCustomerSuggestionIndex] = useState(-1);
  const [customerLookup, setCustomerLookup] = useState({ status: "", message: "" });
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: "", phone: "", address: "" });
  const [newCustomerError, setNewCustomerError] = useState("");
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [rows, setRows] = useState([emptyRow()]);
  const [entries, setEntries] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [editingEntry, setEditingEntry] = useState(null);
  const [paymentTypeUpdating, setPaymentTypeUpdating] = useState({});
  const [entryDeleting, setEntryDeleting] = useState({});
  const [saleRequesting, setSaleRequesting] = useState({});
  const [myRequests, setMyRequests] = useState([]);
  const [openMaterialIndex, setOpenMaterialIndex] = useState(null);
  const [activeMaterialSuggestionIndex, setActiveMaterialSuggestionIndex] = useState(-1);
  const [savedCustomerFilter, setSavedCustomerFilter] = useState("");
  const [savedDateFromFilter, setSavedDateFromFilter] = useState("");
  const [savedDateToFilter, setSavedDateToFilter] = useState("");
  const [savedSalesPage, setSavedSalesPage] = useState(1);
  const [customerDetailsModal, setCustomerDetailsModal] = useState({ open: false, metric: "qty" });
  const [customerDetailsPage, setCustomerDetailsPage] = useState(1);
  const [customerPaidForm, setCustomerPaidForm] = useState(() => makeCustomerPaidForm());
  const [customerPaidSaving, setCustomerPaidSaving] = useState(false);
  const [customerPaidEdit, setCustomerPaidEdit] = useState({ paymentId: "", date: "", amount: "", mode: "Cash" });
  const [customerPaidUpdating, setCustomerPaidUpdating] = useState(false);
  const [customerPaidDeleting, setCustomerPaidDeleting] = useState({});
  const [expandedCustomerPaidDates, setExpandedCustomerPaidDates] = useState({});
  const [closingStockModal, setClosingStockModal] = useState({ open: false, loading: false });
  const [closingStockSourceEntries, setClosingStockSourceEntries] = useState([]);
  const [closingStockSourceDate, setClosingStockSourceDate] = useState("");
  const [closingStockSourceRows, setClosingStockSourceRows] = useState([]);
  const [closingStockSourceError, setClosingStockSourceError] = useState("");
  const skipLookupRef = useRef(false);
  const materialRefs = useRef({});
  const materialSuggestionRefs = useRef([]);
  const customerSuggestionRefs = useRef([]);
  const [materialRects, setMaterialRects] = useState({});

  const refresh = async () => {
    try {
      const list = await getSalesEntries();
      setEntries(list || []);
    } catch (err) {
      console.error(err);
      alert("Failed to load sales.");
    }
  };

  const refreshCustomers = async () => {
    try {
      const list = await getCustomers();
      setCustomers(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
    }
  };

  const refreshPurchaseItems = async () => {
    try {
      const list = await getItems();
      setPurchaseItems(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
    }
  };

  const refreshMyRequests = async () => {
    if (isAdmin) {
      setMyRequests([]);
      return;
    }
    try {
      const list = await getMyBillActionRequests();
      setMyRequests(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { refresh(); refreshCustomers(); refreshPurchaseItems(); refreshMyRequests(); }, []);

  useLayoutEffect(() => {
    const next = {};
    rows.forEach((_, i) => {
      const el = materialRefs.current[i];
      if (el) {
        const r = el.getBoundingClientRect();
        next[i] = r;
      }
    });
    setMaterialRects(next);
  }, [rows, purchaseItems]);

  useEffect(() => {
    if (activeMaterialSuggestionIndex < 0) return;
    const target = materialSuggestionRefs.current[activeMaterialSuggestionIndex];
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "nearest" });
    }
  }, [activeMaterialSuggestionIndex, openMaterialIndex]);
  useEffect(() => {
    if (lockedCategory) {
      setCategory(lockedCategory);
    }
  }, [lockedCategory]);

  const activeCategory = lockedCategory || category;
  const canSwitchCategory = !lockedCategory;
  const showClosingStockOption = false;
  const showPaymentTypeField = activeCategory === "Stall" || activeCategory === "Market";
  const showSavedPaymentTypeColumn = lockedCategory === "Stall" || lockedCategory === "Market";
  const showSavedPrintColumn = lockedCategory === "Stall" || lockedCategory === "Market";
  const showSavedWhatsAppColumn = lockedCategory === "Stall" || lockedCategory === "Market";

  const totals = useMemo(() => {
    const totalAmount = rows.reduce((s, r) => s + (Number(r.qty) * Number(r.rate) || 0), 0);
    const totalQty = rows.reduce((s, r) => s + (Number(r.qty) || 0), 0);
    return { totalAmount: +totalAmount.toFixed(2), totalQty };
  }, [rows]);

  const visibleEntries = useMemo(() => {
    if (!lockedCategory) return entries;
    return entries.filter((en) => {
      if (en.category !== lockedCategory) return false;
      if (lockedCategory === "Stall" && isEveningSalesSource(en?.source)) return false;
      return true;
    });
  }, [entries, lockedCategory]);

  const closingStockAvailableDates = useMemo(() => {
    if (!showClosingStockOption) return [];
    const dates = new Set();
    for (const en of Array.isArray(closingStockSourceEntries) ? closingStockSourceEntries : []) {
      if (String(en?.category || "General") !== activeCategory) continue;
      const dateKey = toComparableYmd(en?.createdAt);
      if (dateKey) dates.add(dateKey);
    }
    return Array.from(dates).sort((a, b) => b.localeCompare(a));
  }, [closingStockSourceEntries, activeCategory, showClosingStockOption]);

  useEffect(() => {
    if (!closingStockModal.open) return;
    if (!showClosingStockOption) return;
    if (!closingStockAvailableDates.length) {
      setClosingStockSourceRows([]);
      return;
    }
    const selectedDate = closingStockAvailableDates.includes(closingStockSourceDate)
      ? closingStockSourceDate
      : closingStockAvailableDates[0];
    if (selectedDate !== closingStockSourceDate) {
      setClosingStockSourceDate(selectedDate);
      return;
    }

    const materialMap = new Map();
    for (const en of closingStockSourceEntries) {
      if (String(en?.category || "General") !== activeCategory) continue;
      if (toComparableYmd(en?.createdAt) !== selectedDate) continue;
      for (const it of Array.isArray(en?.items) ? en.items : []) {
        const material = String(it?.material || "").trim();
        if (!material) continue;
        const key = material.toLowerCase();
        const existing = materialMap.get(key) || { material, closingStock: 0, qty: "", rate: "", amount: 0 };
        existing.closingStock += Number(it?.qty) || 0;
        materialMap.set(key, existing);
      }
    }
    const nextRows = Array.from(materialMap.values())
      .map((row) => ({ ...row, closingStock: +Number(row.closingStock || 0).toFixed(3) }))
      .sort((a, b) => String(a.material || "").localeCompare(String(b.material || ""), undefined, { sensitivity: "base" }));
    setClosingStockSourceRows(nextRows);
  }, [
    closingStockModal.open,
    showClosingStockOption,
    closingStockAvailableDates,
    closingStockSourceDate,
    closingStockSourceEntries,
    activeCategory,
  ]);

  const showSavedCustomerFilter = activeCategory === "Stall" || activeCategory === "Market";

  const dateFilteredVisibleEntries = useMemo(() => {
    const fromDate = toComparableYmd(savedDateFromFilter);
    const toDate = toComparableYmd(savedDateToFilter);

    return visibleEntries.filter((en) => {
      const entryDate = toComparableYmd(en?.saleDate);
      if (fromDate && (!entryDate || entryDate < fromDate)) return false;
      if (toDate && (!entryDate || entryDate > toDate)) return false;
      return true;
    });
  }, [visibleEntries, savedDateFromFilter, savedDateToFilter]);

  const customerFilterOptions = useMemo(() => {
    if (!showSavedCustomerFilter) return [];
    return Array.from(new Set(dateFilteredVisibleEntries.map((en) => salesCustomerFilterLabel(en)).filter(Boolean))).sort(
      (a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [dateFilteredVisibleEntries, showSavedCustomerFilter]);

  useEffect(() => {
    if (!savedCustomerFilter) return;
    if (!customerFilterOptions.includes(savedCustomerFilter)) {
      setSavedCustomerFilter("");
    }
  }, [savedCustomerFilter, customerFilterOptions]);

  useEffect(() => {
    if (!showSavedCustomerFilter || !savedCustomerFilter) {
      setCustomerDetailsModal((prev) => (prev.open ? { ...prev, open: false } : prev));
      setCustomerDetailsPage(1);
      setCustomerPaidForm(makeCustomerPaidForm());
      setCustomerPaidSaving(false);
      setCustomerPaidEdit({ paymentId: "", date: "", amount: "", mode: "Cash" });
      setCustomerPaidUpdating(false);
      setCustomerPaidDeleting({});
      setExpandedCustomerPaidDates({});
    }
  }, [showSavedCustomerFilter, savedCustomerFilter]);

  const savedSalesEntries = useMemo(() => {
    if (!showSavedCustomerFilter || !savedCustomerFilter) return dateFilteredVisibleEntries;
    return dateFilteredVisibleEntries.filter((en) => salesCustomerFilterLabel(en) === savedCustomerFilter);
  }, [dateFilteredVisibleEntries, showSavedCustomerFilter, savedCustomerFilter]);

  const savedSalesTotalPages = Math.max(1, Math.ceil(savedSalesEntries.length / SALES_SAVED_ENTRIES_PAGE_SIZE));

  const pagedSavedSalesEntries = useMemo(() => {
    const start = (savedSalesPage - 1) * SALES_SAVED_ENTRIES_PAGE_SIZE;
    return savedSalesEntries.slice(start, start + SALES_SAVED_ENTRIES_PAGE_SIZE);
  }, [savedSalesEntries, savedSalesPage]);

  const savedSalesPageStart =
    savedSalesEntries.length === 0 ? 0 : (savedSalesPage - 1) * SALES_SAVED_ENTRIES_PAGE_SIZE + 1;
  const savedSalesPageEnd =
    savedSalesEntries.length === 0
      ? 0
      : Math.min((savedSalesPage - 1) * SALES_SAVED_ENTRIES_PAGE_SIZE + pagedSavedSalesEntries.length, savedSalesEntries.length);

  useEffect(() => {
    setSavedSalesPage(1);
  }, [savedCustomerFilter, savedDateFromFilter, savedDateToFilter, activeCategory]);

  useEffect(() => {
    setSavedSalesPage((current) => Math.min(current, savedSalesTotalPages));
  }, [savedSalesTotalPages]);

  const selectedCustomerTotals = useMemo(() => {
    if (!showSavedCustomerFilter || !savedCustomerFilter) return null;
    return savedSalesEntries.reduce(
      (acc, en) => {
        const qty = Number(en?.totalQty) || 0;
        const settlementSummary = getSettlementSummary(Number(en?.total) || 0, en?.payments);
        const amount = settlementSummary.totalAmount;
        const paid = settlementSummary.paidAmount;
        acc.qty += qty;
        acc.amount += amount;
        acc.paid += paid;
        acc.balance += settlementSummary.pendingAmount;
        acc.entryCount += 1;
        return acc;
      },
      { qty: 0, amount: 0, paid: 0, balance: 0, entryCount: 0 }
    );
  }, [savedSalesEntries, showSavedCustomerFilter, savedCustomerFilter]);

  const selectedCustomerDateWiseRows = useMemo(() => {
    if (!showSavedCustomerFilter || !savedCustomerFilter) return [];
    const map = new Map();
    for (const en of savedSalesEntries) {
      const dateKey = toComparableYmd(en?.saleDate) || "Unknown Date";
      const qty = Number(en?.totalQty) || 0;
      const settlementSummary = getSettlementSummary(Number(en?.total) || 0, en?.payments);
      const amount = settlementSummary.totalAmount;
      const paid = settlementSummary.paidAmount;
      const existing = map.get(dateKey) || {
        date: dateKey,
        entryCount: 0,
        totalQty: 0,
        totalAmount: 0,
        totalPaid: 0,
        balance: 0,
      };
      existing.entryCount += 1;
      existing.totalQty += qty;
      existing.totalAmount += amount;
      existing.totalPaid += paid;
      existing.balance += settlementSummary.pendingAmount;
      map.set(dateKey, existing);
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.date === "Unknown Date") return 1;
      if (b.date === "Unknown Date") return -1;
      return String(a.date).localeCompare(String(b.date));
    });
  }, [savedSalesEntries, showSavedCustomerFilter, savedCustomerFilter]);

  const selectedCustomerPaidDateWiseRows = useMemo(() => {
    if (!showSavedCustomerFilter || !savedCustomerFilter) return [];
    const map = new Map();
    for (const en of savedSalesEntries) {
      for (const payment of Array.isArray(en?.payments) ? en.payments : []) {
        if (!isPaymentSettlement(payment)) continue;
        const amount = Number(payment?.amount) || 0;
        if (amount <= 0) continue;
        const date = toComparableYmd(payment?.date) || "";
        if (!date) continue;
        const mode = normalizeSalesPaymentType(payment?.mode || "Cash");
        const paymentRow = {
          paymentId: String(payment?.id || ""),
          entryId: String(en?.id || ""),
          date,
          saleDate: toComparableYmd(en?.saleDate) || "",
          totalPaid: amount,
          mode,
          createdAt: String(payment?.created_at || payment?.createdAt || ""),
        };
        if (!map.has(date)) {
          map.set(date, {
            date,
            totalPaid: 0,
            paymentCount: 0,
            modes: new Set(),
            items: [],
          });
        }
        const bucket = map.get(date);
        bucket.totalPaid += amount;
        bucket.paymentCount += 1;
        bucket.modes.add(mode);
        bucket.items.push(paymentRow);
      }
    }
    return Array.from(map.values())
      .map((row) => {
        const items = [...row.items].sort((a, b) => {
          const createdA = String(a.createdAt || "");
          const createdB = String(b.createdAt || "");
          if (createdA !== createdB) return createdA.localeCompare(createdB);
          return String(a.paymentId || "").localeCompare(String(b.paymentId || ""));
        });
        return {
          date: row.date,
          totalPaid: +Number(row.totalPaid || 0).toFixed(2),
          paymentCount: row.paymentCount,
          modesText: Array.from(row.modes).join(", ") || "Cash",
          items,
          singlePayment: items.length === 1 ? items[0] : null,
        };
      })
      .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  }, [savedSalesEntries, showSavedCustomerFilter, savedCustomerFilter]);

  const customerPaidOnlyModal = customerDetailsModal.metric === "paid";
  const customerAmountModal = customerDetailsModal.metric === "amount";
  const customerDetailsRowsForModal = customerPaidOnlyModal
    ? selectedCustomerPaidDateWiseRows
    : selectedCustomerDateWiseRows;
  const customerDetailsTotalPages = Math.max(
    1,
    Math.ceil(customerDetailsRowsForModal.length / SALES_CUSTOMER_MODAL_PAGE_SIZE)
  );
  const effectiveCustomerDetailsPage = Math.min(customerDetailsPage, customerDetailsTotalPages);
  const customerDetailsPageStart =
    customerDetailsRowsForModal.length === 0 ? 0 : (effectiveCustomerDetailsPage - 1) * SALES_CUSTOMER_MODAL_PAGE_SIZE + 1;
  const customerDetailsPageEnd = Math.min(
    effectiveCustomerDetailsPage * SALES_CUSTOMER_MODAL_PAGE_SIZE,
    customerDetailsRowsForModal.length
  );
  const customerDetailsPagedRows = customerDetailsRowsForModal.slice(
    (effectiveCustomerDetailsPage - 1) * SALES_CUSTOMER_MODAL_PAGE_SIZE,
    effectiveCustomerDetailsPage * SALES_CUSTOMER_MODAL_PAGE_SIZE
  );
  const customerDetailsColSpan = customerPaidOnlyModal ? 5 : customerAmountModal ? 5 : 6;

  const requestByEntry = useMemo(() => {
    const map = {};
    for (const req of Array.isArray(myRequests) ? myRequests : []) {
      const entryId = req?.entryId;
      const action = req?.action;
      if (!entryId || !action) continue;
      if (!map[entryId]) map[entryId] = {};
      if (!map[entryId][action]) {
        map[entryId][action] = req;
      }
    }
    return map;
  }, [myRequests]);

  const requestKey = (entryId, action) => `${String(entryId)}:${String(action)}`;

  useEffect(() => {
    if (customerDetailsPage > customerDetailsTotalPages) {
      setCustomerDetailsPage(customerDetailsTotalPages);
    }
  }, [customerDetailsPage, customerDetailsTotalPages]);

  const lookupCustomerByPhone = async (phone) => {
    const trimmed = String(phone || "").trim();
    if (!trimmed || trimmed.length < 10) {
      setCustomerLookup({ status: "", message: "" });
      return;
    }
    setCustomerLookup({ status: "loading", message: "Searching..." });
    try {
      const data = await getCustomerByPhone(trimmed);
      setCustomerName(data?.name || "");
      setCustomerAddress(data?.address || "");
      setCustomerLookup({ status: "found", message: "Customer found" });
    } catch (err) {
      console.error(err);
      const notFound = err?.message === "Not found";
      if (notFound) {
        setCustomerName("");
        setCustomerAddress("");
        setNewCustomer({ name: "", phone: trimmed, address: "" });
        setNewCustomerError("");
        setShowCustomerModal(true);
      }
      const message = notFound ? "Customer not found" : "Failed to fetch customer";
      setCustomerLookup({ status: "notfound", message });
    }
  };

  useEffect(() => {
    const phoneQuery = customerPhone.trim().toLowerCase();
    const nameQuery = customerName.trim().toLowerCase();
    if (skipLookupRef.current) {
      skipLookupRef.current = false;
      setCustomerSuggestions([]);
      return undefined;
    }
    const query = phoneQuery || nameQuery;
    const matches = query
      ? customers
          .filter(
            (c) =>
              String(c.phone || "").toLowerCase().includes(query) ||
              String(c.name || "").toLowerCase().includes(query)
          )
          .slice(0, 8)
      : [];
    setCustomerSuggestions(matches);
    if (!phoneQuery || phoneQuery.length < 10) {
      setCustomerLookup({ status: "", message: "" });
      return undefined;
    }
    const timer = setTimeout(() => {
      lookupCustomerByPhone(customerPhone.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [customerPhone, customerName, customers]);

  useEffect(() => {
    setCustomerSuggestionIndex(customerSuggestions.length ? 0 : -1);
  }, [customerSuggestions]);

  useEffect(() => {
    if (customerSuggestionIndex < 0) return;
    const target = customerSuggestionRefs.current[customerSuggestionIndex];
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "nearest" });
    }
  }, [customerSuggestionIndex, customerSuggestions]);

  const handleChange = (i, field, value) => {
    if (field === "material") {
      setOpenMaterialIndex(value.trim().length >= 2 ? i : null);
      setActiveMaterialSuggestionIndex(value.trim().length >= 2 ? 0 : -1);
    }
    setRows((prev) => {
      const next = [...prev];
      const updated = { ...next[i], [field]: value };
      const qty = Number(updated.qty) || 0;
      const rate = Number(updated.rate) || 0;
      updated.amount = +(qty * rate).toFixed(2);
      next[i] = updated;
      return next;
    });
  };

  const handleSelectItem = (index, item) => {
    setRows((prev) => {
      const next = [...prev];
      const target = { ...next[index] };
      const rate = item?.sellingPrice ?? target.rate;
      target.material = item?.name || item?.code || "";
      target.rate = rate;
      const qty = Number(target.qty) || 0;
      target.amount = +(qty * (Number(rate) || 0)).toFixed(2);
      next[index] = target;
      return next;
    });
    setOpenMaterialIndex(null);
    setActiveMaterialSuggestionIndex(-1);
  };

  const focusMaterial = (rowIndex) => {
    const el = document.querySelector(`[data-material-row="${rowIndex}"]`);
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const focusQty = (rowIndex) => {
    const el = document.querySelector(`[data-qty-row="${rowIndex}"]`);
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const focusRate = (rowIndex) => {
    const el = document.querySelector(`[data-rate-row="${rowIndex}"]`);
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const focusNextRowMaterial = (currentRow) => {
    const next = currentRow + 1;
    focusMaterial(next);
  };

  const addRow = () => setRows((p) => [...p, emptyRow()]);
  const removeRow = (i) => setRows((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : p));

  const resetForm = () => {
    setRows([emptyRow()]);
    setPaymentType("Any");
    setCustomerPhone("");
    setCustomerName("");
    setCustomerAddress("");
    setCustomerLookup({ status: "", message: "" });
    setCustomerSuggestions([]);
    setActiveMaterialSuggestionIndex(-1);
    setSaleDate(new Date().toISOString().slice(0, 10));
    setEditingEntry(null);
    if (canSwitchCategory) {
      setCategory(lockedCategory || "Stall");
    }
  };

  const startEditEntry = (entry) => {
    if (!entry?.id) return;
    setEditingEntry(entry);
    setSaleDate(entry.saleDate || new Date().toISOString().slice(0, 10));
    setPaymentType(normalizeSalesPaymentType(entry?.paymentType));

    const lines = String(entry.customer || "")
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    const name = lines[0] || "";
    const phone = lines[1] || "";
    const address = lines.slice(2).join("\n");
    setCustomerName(name);
    setCustomerPhone(phone);
    setCustomerAddress(address);
    setCustomerLookup({ status: name || phone ? "found" : "", message: "" });
    setCustomerSuggestions([]);

    if (canSwitchCategory) {
      setCategory(entry.category || "Stall");
    }
    setRows(
      (entry.items || []).map((it) => {
        const qty = Number(it?.qty) || 0;
        const rate = Number(it?.rate) || 0;
        return {
          material: it?.material || "",
          qty,
          rate,
          amount: +(qty * rate).toFixed(2),
          closingStock: Number(it?.closingStock ?? it?.closing_stock ?? 0) || 0,
        };
      })
    );
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      // ignore
    }
  };

  const applyCustomerSuggestion = (cust) => {
    const phone = cust?.phone || "";
    const name = cust?.name || "";
    const address = cust?.address || "";
    skipLookupRef.current = true;
    setCustomerPhone(phone);
    setCustomerName(name);
    setCustomerAddress(address);
    setCustomerLookup({ status: "found", message: "Customer selected" });
    setCustomerSuggestions([]);
    setCustomerSuggestionIndex(-1);
  };

  const requestEntryAction = async (entry, action) => {
    if (!entry?.id) return;
    if (!window.confirm(`Send ${action} request to admin for this sale?`)) return;
    const key = requestKey(entry.id, action);
    setSaleRequesting((prev) => ({ ...prev, [key]: true }));
    try {
      await createBillActionRequest(entry.id, action);
      await refreshMyRequests();
      alert("Request sent to admin.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to send request.");
    } finally {
      setSaleRequesting((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const customerSource = useMemo(
    () => (lockedCategory ? `SalesEntry-${lockedCategory}` : "SalesEntry"),
    [lockedCategory]
  );

  const handleSaveCustomer = async () => {
    const name = newCustomer.name.trim();
    const phone = newCustomer.phone.trim();
    const address = newCustomer.address.trim();
    if (!name || !phone) {
      setNewCustomerError("Customer name and mobile number are required.");
      return;
    }
    try {
      const saved = await addCustomer({ name, phone, address, source: customerSource });
      const savedId = saved?.id || Math.random().toString(36).slice(2);
      setCustomers((prev) => [{ id: savedId, name, phone, address }, ...(prev || [])]);
      applyCustomerSuggestion({ id: savedId, name, phone, address });
      setShowCustomerModal(false);
      setNewCustomerError("");
    } catch (err) {
      console.error(err);
      const message = err?.message === "Phone already exists" ? "Phone already exists." : "Failed to save customer.";
      setNewCustomerError(message);
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!entry?.id) return;
    if (!window.confirm(`Delete sale dated ${formatDisplayDate(entry.saleDate)}?`)) return;
    setEntryDeleting((prev) => ({ ...prev, [entry.id]: true }));
    try {
      await deleteSalesEntry(entry.id);
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[entry.id];
        return next;
      });
      await refresh();
      await refreshMyRequests();
      if (editingEntry?.id === entry.id) {
        resetForm();
      }
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to delete sale.");
    } finally {
      setEntryDeleting((prev) => {
        const next = { ...prev };
        delete next[entry.id];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!window.confirm(editingEntry ? "Update this sale?" : "Save this sale?")) {
      return;
    }
    const nameText = customerName.trim();
    const phoneText = customerPhone.trim();
    const addressText = customerAddress.trim();
    const customerText = [nameText, phoneText, addressText].filter(Boolean).join("\n");
    const invalidRow = rows.find(
      (r) =>
        (r.material || "").trim() &&
        !purchaseItems.some(
          (it) =>
            String(it.name || "").toLowerCase() === String(r.material || "").trim().toLowerCase() ||
            String(it.code || "").toLowerCase() === String(r.material || "").trim().toLowerCase()
        )
    );
    if (invalidRow) {
      alert("Please select items only from the dropdown list.");
      return;
    }

    const items = rows
      .filter((r) => r.material || r.qty || r.rate)
      .map((r) => ({
        material: r.material || "",
        qty: Number(r.qty) || 0,
        rate: Number(r.rate) || 0,
        amount: Number(r.amount) || 0,
        closingStock: showClosingStockOption ? Number(r.closingStock) || 0 : 0,
      }));
    if (items.length === 0) { alert("Please enter at least one line item."); return; }
    const total = items.reduce((s, it) => s + it.amount, 0);
    const totalQty = items.reduce((s, it) => s + it.qty, 0);
    const payload = {
      category: activeCategory,
      saleDate,
      customer: customerText,
      items,
      paymentType: showPaymentTypeField ? normalizeSalesPaymentType(paymentType) : "Any",
      total,
      totalQty,
    };
    const saveCall = editingEntry?.id
      ? () => updateSalesEntry(editingEntry.id, payload)
      : () => addSalesEntry({ ...payload, payments: [] });
    try {
      await saveCall();
      await refresh();
      await refreshMyRequests();
      resetForm();
    } catch (err) {
      console.error(err);
      alert("Failed to save sale.");
    }
  };

  const setSavedEntryPaymentType = async (entryId, prevType, nextTypeRaw) => {
    const prevValue = normalizeSalesPaymentType(prevType);
    const nextValue = normalizeSalesPaymentType(nextTypeRaw);
    if (!entryId || prevValue === nextValue) return;

    setEntries((prev) =>
      prev.map((en) => (en.id === entryId ? { ...en, paymentType: nextValue, ...(nextValue === "Credit" ? { paymentDone: false } : {}) } : en))
    );
    setEditingEntry((prev) =>
      prev && prev.id === entryId
        ? { ...prev, paymentType: nextValue, ...(nextValue === "Credit" ? { paymentDone: false } : {}) }
        : prev
    );
    setPaymentTypeUpdating((prev) => ({ ...prev, [entryId]: true }));
    try {
      const res = await updateSalesEntryPaymentType(entryId, nextValue);
      setEntries((prev) =>
        prev.map((en) =>
          en.id === entryId
            ? {
                ...en,
                paymentType: normalizeSalesPaymentType(res?.paymentType || nextValue),
                ...(typeof res?.paymentDone === "boolean" ? { paymentDone: res.paymentDone } : {}),
              }
            : en
        )
      );
      setEditingEntry((prev) =>
        prev && prev.id === entryId
          ? {
              ...prev,
              paymentType: normalizeSalesPaymentType(res?.paymentType || nextValue),
              ...(typeof res?.paymentDone === "boolean" ? { paymentDone: res.paymentDone } : {}),
            }
          : prev
      );
    } catch (err) {
      console.error(err);
      setEntries((prev) =>
        prev.map((en) => (en.id === entryId ? { ...en, paymentType: prevValue } : en))
      );
      setEditingEntry((prev) => (prev && prev.id === entryId ? { ...prev, paymentType: prevValue } : prev));
      alert("Failed to update payment type.");
    } finally {
      setPaymentTypeUpdating((prev) => {
        const next = { ...prev };
        delete next[entryId];
        return next;
      });
    }
  };

  const handlePrintEntry = async (entry) => {
    if (!entry) return;
    const customerLines = String(entry?.customer || "")
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    try {
      await printReceiptWindow({
        billNo: formatSalesReceiptNo(entry),
        billDate: formatDisplayDate(entry?.saleDate),
        billTime: "",
        customerLines,
        items: Array.isArray(entry?.items) ? entry.items : [],
        totalQty: Number(entry?.totalQty) || 0,
        totalAmount: Number(entry?.total) || 0,
      });
    } catch (err) {
      console.error(err);
      alert("Failed to print sale.");
    }
  };

  const handleSendWhatsApp = (entry) => {
    if (!entry) return;
    const customerText = entry.customer || "";
    const phone = extractPhone(customerText);
    if (!phone) {
      alert("No valid customer phone found for this sale.");
      return;
    }

    const customerLines = String(customerText)
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    buildReceiptHtmlDocument({
      billNo: formatSalesReceiptNo(entry),
      billDate: formatDisplayDate(entry?.saleDate),
      billTime: "",
      customerLines,
      items: Array.isArray(entry?.items) ? entry.items : [],
      totalQty: Number(entry?.totalQty) || 0,
      totalAmount: Number(entry?.total) || 0,
    })
      .then((html) =>
        saveReceiptHtml(html).then((res) => ({
          res,
          billNo: formatSalesReceiptNo(entry),
          billDate: formatDisplayDate(entry?.saleDate),
          customerLines,
          items: Array.isArray(entry?.items) ? entry.items : [],
          totalQty: Number(entry?.totalQty) || 0,
          totalAmount: Number(entry?.total) || 0,
        }))
      )
      .then(({ res, billNo, billDate, customerLines, items, totalQty, totalAmount }) => {
        let receiptLink = "";
        if (res?.url) {
          if (String(res.url).startsWith("http")) {
            receiptLink = res.url;
          } else {
            const root = getApiBaseRoot().replace(/\/$/, "");
            const raw = String(res.url);
            const tail = raw.includes("/receipts/")
              ? raw.substring(raw.indexOf("/receipts/"))
              : `/${raw.replace(/^\/+/, "")}`;
            receiptLink = `${root}${tail}`;
          }
        }

        const waText = buildWhatsAppText({
          billNo,
          billDate,
          customerLines,
          items,
          totalQty,
          totalAmount,
          receiptLink,
        });
        const url = `https://wa.me/${phone}?text=${waText}`;
        const win = window.open(url, "_blank");
        if (!win) alert("Please allow popups to send the WhatsApp message.");
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to prepare receipt for WhatsApp.");
      });
  };

  const handleAddCustomerPaidFromModal = async () => {
    if (!savedCustomerFilter) {
      alert("Please select a customer.");
      return;
    }

    const amount = Number(customerPaidForm.amount) || 0;
    const today = new Date().toISOString().slice(0, 10);
    const date = String(customerPaidForm.date || "").trim() || today;
    if (amount <= 0) {
      alert("Enter a valid paid amount.");
      return;
    }
    if (date > today) {
      alert("Payment date cannot be in the future.");
      return;
    }

    const pendingEntries = [...savedSalesEntries]
      .map((en) => {
        const balance = getSettlementSummary(Number(en?.total || 0), en?.payments).pendingAmount;
        return { entry: en, balance };
      })
      .filter((row) => row.entry?.id && row.balance > 0)
      .sort((a, b) => {
        const dateCmp = toComparableYmd(a.entry?.saleDate).localeCompare(toComparableYmd(b.entry?.saleDate));
        if (dateCmp !== 0) return dateCmp;
        return String(a.entry?.id || "").localeCompare(String(b.entry?.id || ""));
      });

    if (pendingEntries.length === 0) {
      alert("No pending balance available for this customer.");
      return;
    }

    const totalPending = pendingEntries.reduce((sum, row) => sum + row.balance, 0);
    if (amount > totalPending + 0.0001) {
      alert(`Amount exceeds pending balance (${totalPending.toFixed(2)}).`);
      return;
    }

    const allocations = [];
    let remaining = Number(amount.toFixed(2));
    for (let i = 0; i < pendingEntries.length && remaining > 0.0001; i += 1) {
      const row = pendingEntries[i];
      const isLast = i === pendingEntries.length - 1;
      const alloc = Math.min(row.balance, isLast ? remaining : Number(remaining.toFixed(2)));
      const rounded = Number(Math.min(row.balance, alloc).toFixed(2));
      if (rounded <= 0) continue;
      allocations.push({ entryId: row.entry.id, amount: rounded });
      remaining = Number((remaining - rounded).toFixed(2));
    }

    if (remaining > 0.0001) {
      alert("Unable to allocate full payment amount. Please try a smaller amount.");
      return;
    }

    setCustomerPaidSaving(true);
    let appliedCount = 0;
    try {
      for (const part of allocations) {
        await addSalesPayment(part.entryId, { amount: part.amount, date, mode: "Cash" });
        appliedCount += 1;
      }
      await refresh();
      setCustomerDetailsPage(1);
      setCustomerPaidForm((prev) => ({ ...prev, amount: "" }));
      alert(`Payment added successfully (${amount.toFixed(2)}) across ${appliedCount} sale${appliedCount === 1 ? "" : "s"}.`);
    } catch (err) {
      console.error(err);
      await refresh();
      alert(
        appliedCount > 0
          ? "Some payments were added before an error occurred. Data has been refreshed."
          : "Failed to add payment."
      );
    } finally {
      setCustomerPaidSaving(false);
    }
  };

  const handleStartEditCustomerPaid = (row) => {
    if (!row?.paymentId) {
      alert("Unable to edit this payment.");
      return;
    }
    setCustomerPaidEdit({
      paymentId: String(row.paymentId),
      date: String(row.date || new Date().toISOString().slice(0, 10)),
      amount: Number(row.totalPaid || 0).toFixed(2),
      mode: normalizeSalesPaymentType(row.mode || "Cash"),
    });
  };

  const handleCancelEditCustomerPaid = () => {
    setCustomerPaidEdit({ paymentId: "", date: "", amount: "", mode: "Cash" });
    setCustomerPaidUpdating(false);
  };

  const handleDeleteCustomerPaid = async (row) => {
    const paymentId = String(row?.paymentId || "").trim();
    if (!paymentId) {
      alert("Unable to delete this payment.");
      return;
    }
    const amountText = Number(row?.totalPaid || 0).toFixed(2);
    const dateText = formatDisplayDate(row?.date);
    if (!window.confirm(`Delete paid entry of ${amountText} on ${dateText}?`)) {
      return;
    }

    setCustomerPaidDeleting((prev) => ({ ...prev, [paymentId]: true }));
    try {
      await deleteSalesPayment(paymentId);
      await refresh();
      if (customerPaidEdit.paymentId === paymentId) {
        setCustomerPaidEdit({ paymentId: "", date: "", amount: "", mode: "Cash" });
        setCustomerPaidUpdating(false);
      }
      alert("Paid entry deleted.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to delete paid entry.");
    } finally {
      setCustomerPaidDeleting((prev) => {
        const next = { ...prev };
        delete next[paymentId];
        return next;
      });
    }
  };

  const handleSaveEditCustomerPaid = async () => {
    const paymentId = String(customerPaidEdit.paymentId || "").trim();
    if (!paymentId) return;
    const amount = Number(customerPaidEdit.amount) || 0;
    const today = new Date().toISOString().slice(0, 10);
    const date = String(customerPaidEdit.date || "").trim() || today;
    if (amount <= 0) {
      alert("Enter a valid paid amount.");
      return;
    }
    if (date > today) {
      alert("Payment date cannot be in the future.");
      return;
    }

    setCustomerPaidUpdating(true);
    try {
      await updateSalesPayment(paymentId, {
        amount: Number(amount.toFixed(2)),
        date,
        mode: normalizeSalesPaymentType(customerPaidEdit.mode || "Cash"),
      });
      await refresh();
      setCustomerPaidEdit({ paymentId: "", date: "", amount: "", mode: "Cash" });
      alert("Paid amount updated.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to update paid amount.");
    } finally {
      setCustomerPaidUpdating(false);
    }
  };

  const openCustomerDetailsModal = (metric) => {
    if (!savedCustomerFilter) return;
    setCustomerDetailsModal({ open: true, metric });
    setCustomerDetailsPage(1);
    if (metric === "paid") {
      setCustomerPaidForm(makeCustomerPaidForm());
      setCustomerPaidEdit({ paymentId: "", date: "", amount: "", mode: "Cash" });
      setCustomerPaidUpdating(false);
      setCustomerPaidDeleting({});
      setExpandedCustomerPaidDates({});
    }
  };

  const closeCustomerDetailsModal = () => {
    setCustomerDetailsModal((prev) => ({ ...prev, open: false }));
    setCustomerDetailsPage(1);
    setCustomerPaidForm(makeCustomerPaidForm());
    setCustomerPaidSaving(false);
    setCustomerPaidEdit({ paymentId: "", date: "", amount: "", mode: "Cash" });
    setCustomerPaidUpdating(false);
    setCustomerPaidDeleting({});
    setExpandedCustomerPaidDates({});
  };

  const handleClosingStockModalRowChange = (index, field, value) => {
    setClosingStockSourceRows((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };
      const qty = Number(updated.qty) || 0;
      const rate = Number(updated.rate) || 0;
      updated.amount = +(qty * rate).toFixed(2);
      next[index] = updated;
      return next;
    });
  };

  const openClosingStockModal = async () => {
    if (!showClosingStockOption) return;
    setClosingStockModal({ open: true, loading: true });
    setClosingStockSourceError("");
    try {
      const list = await getInwardEntries();
      const entriesList = Array.isArray(list) ? list : [];
      setClosingStockSourceEntries(entriesList);

      const dates = Array.from(
        new Set(
          entriesList
            .filter((en) => String(en?.category || "General") === activeCategory)
            .map((en) => toComparableYmd(en?.createdAt))
            .filter(Boolean)
        )
      ).sort((a, b) => b.localeCompare(a));

      const preferredDate = dates.includes(saleDate) ? saleDate : dates[0] || "";
      setClosingStockSourceDate(preferredDate);
      if (!dates.length) {
        setClosingStockSourceRows([]);
        setClosingStockSourceError(`No ${activeCategory} inward entries found.`);
      }
    } catch (err) {
      console.error(err);
      setClosingStockSourceEntries([]);
      setClosingStockSourceRows([]);
      setClosingStockSourceError("Failed to load inward entries.");
    } finally {
      setClosingStockModal({ open: true, loading: false });
    }
  };

  const closeClosingStockModal = () => {
    setClosingStockModal({ open: false, loading: false });
    setClosingStockSourceError("");
    setClosingStockSourceRows([]);
  };

  const applyClosingStockRowsToForm = () => {
    const preparedRows = closingStockSourceRows
      .filter((row) => String(row?.material || "").trim())
      .map((row) => ({
        material: String(row.material || "").trim(),
        qty: row.qty === "" ? "" : row.qty,
        rate: row.rate === "" ? "" : row.rate,
        amount: Number(row.amount) || 0,
        closingStock: Number(row.closingStock) || 0,
      }));

    if (!preparedRows.length) {
      alert("No materials found for selected inward date.");
      return;
    }

    const currentHasData = rows.some((r) => (r.material || r.qty || r.rate));
    if (currentHasData && !window.confirm("Replace current sales rows with closing stock rows?")) {
      return;
    }

    setRows(preparedRows);
    if (closingStockSourceDate) {
      setSaleDate(closingStockSourceDate);
    }
    closeClosingStockModal();
  };

  return (
    <div className="stock-inward-container" style={{paddingTop: 0}}>
      <div className="form-card">
        <div className="card-header">
          <h2 className="card-title">New Sale</h2>
          <div className="entry-count">{activeCategory}</div>
        </div>
        {/* per-row suggestion lists are rendered inline for filtering */}
        <form className="inward-form" onSubmit={handleSubmit}>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom:'12px'}}>
            <div className="field">
              <label>Category</label>
              {canSwitchCategory ? (
                <div style={{display:'flex', gap: '8px'}}>
                  {['Stall','Market','Delivery'].map((c) => (
                    <button key={c} type="button" className={`btn-secondary${category===c? ' btn-primary': ''}`} onClick={() => setCategory(c)}>{c}</button>
                  ))}
                </div>
              ) : (
                <input className="input-field" type="text" value={activeCategory} readOnly />
              )}
            </div>
            <div className="field">
              <label>Sale Date</label>
              <input className="input-field" type="date" value={saleDate} onChange={(e)=> setSaleDate(e.target.value)} />
            </div>
            <div className="field">
              <label>Customer Phone</label>
              <div style={{ position: "relative" }}>
                <input
                  className="input-field"
                  type="text"
                  inputMode="numeric"
                  value={customerPhone}
                  onChange={(e)=> setCustomerPhone(e.target.value)}
                  onKeyDown={(e)=> {
                    if (e.key === "ArrowDown" && customerSuggestions.length > 0) {
                      e.preventDefault();
                      setCustomerSuggestionIndex((prev) => Math.min(prev + 1, customerSuggestions.length - 1));
                      return;
                    }
                    if (e.key === "ArrowUp" && customerSuggestions.length > 0) {
                      e.preventDefault();
                      setCustomerSuggestionIndex((prev) => Math.max(prev - 1, 0));
                      return;
                    }
                    if (e.key === "Escape") {
                      setCustomerSuggestions([]);
                      setCustomerSuggestionIndex(-1);
                      return;
                    }
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (customerSuggestions.length > 0) {
                        applyCustomerSuggestion(
                          customerSuggestions[
                            customerSuggestionIndex >= 0 ? customerSuggestionIndex : 0
                          ]
                        );
                        return;
                      }
                      lookupCustomerByPhone(customerPhone);
                    }
                  }}
                  placeholder="Phone number"
                  autoComplete="off"
                />
                {customerSuggestions.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: "#fff",
                      border: "1px solid #cbd5e1",
                      borderRadius: 6,
                      marginTop: 4,
                      zIndex: 20,
                      maxHeight: 180,
                      overflowY: "auto",
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    }}
                  >
                    {customerSuggestions.map((c, index) => (
                      <button
                        key={c.id}
                        type="button"
                        className="dropdown-item"
                        ref={(el) => {
                          customerSuggestionRefs.current[index] = el;
                        }}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "8px 10px",
                          border: "none",
                          background: index === customerSuggestionIndex ? "#eff6ff" : "transparent",
                          cursor: "pointer",
                        }}
                        onMouseEnter={() => setCustomerSuggestionIndex(index)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          applyCustomerSuggestion(c);
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{c.name || "Unnamed"}</div>
                        <div style={{ fontSize: "0.8rem", color: "#475569" }}>{c.phone}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {customerLookup.message ? (
                <div style={{ marginTop: 4, fontSize: "0.75rem", color: customerLookup.status === "found" ? "#16a34a" : customerLookup.status === "notfound" ? "#b91c1c" : "#64748b" }}>
                  {customerLookup.message}
                </div>
              ) : null}
            </div>
          </div>

          <div style={{display:'grid', gridTemplateColumns:'repeat(3, minmax(0, 1fr))', gap: '12px', marginBottom:'12px'}}>
            <div className="field">
              <label>Customer Name</label>
              <input className="input-field" type="text" value={customerName} onChange={(e)=> setCustomerName(e.target.value)} placeholder="Name" />
            </div>
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label>Address (optional)</label>
              <textarea className="input-field" rows={1} value={customerAddress} onChange={(e)=> setCustomerAddress(e.target.value)} placeholder="Address" />
            </div>
          </div>

          <div className="table-container">
            <table className="modern-table">
              <colgroup>
                <col style={{width:'50%'}} />
                <col style={{width:'15%'}} />
                <col style={{width:'15%'}} />
                <col style={{width:'20%'}} />
                <col style={{width:'50px'}} />
              </colgroup>
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const q = (row.material || "").toLowerCase();
                  const hasQuery = q.length >= 2;
                  const materialOptions = hasQuery
                    ? purchaseItems
                        .filter(
                          (it) =>
                            ((it.name || "").toLowerCase().includes(q)) ||
                            ((it.code || "").toLowerCase().includes(q))
                        )
                        .slice(0, 20)
                    : [];
                  return (
                    <tr key={i}>
                    <td>
                      <div style={{ position: "relative", zIndex: 10 }} ref={(el)=> { materialRefs.current[i] = el; }}>
                        <input
                          className="input-field"
                          type="text"
                          placeholder="Item / Fish"
                          value={row.material}
                          onChange={(e)=>handleChange(i,'material',e.target.value)}
                          autoComplete="off"
                          data-material-row={i}
                          onKeyDown={(e) => {
                            if (e.key === "ArrowDown" && materialOptions.length > 0) {
                              e.preventDefault();
                              setOpenMaterialIndex(i);
                              setActiveMaterialSuggestionIndex((prev) =>
                                openMaterialIndex === i ? Math.min(prev + 1, materialOptions.length - 1) : 0
                              );
                              return;
                            }
                            if (e.key === "ArrowUp" && materialOptions.length > 0) {
                              e.preventDefault();
                              setOpenMaterialIndex(i);
                              setActiveMaterialSuggestionIndex((prev) =>
                                openMaterialIndex === i ? Math.max(prev - 1, 0) : 0
                              );
                              return;
                            }
                            if (e.key === "Escape") {
                              setOpenMaterialIndex(null);
                              setActiveMaterialSuggestionIndex(-1);
                              return;
                            }
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (openMaterialIndex === i && materialOptions.length > 0) {
                                const picked =
                                  materialOptions[
                                    activeMaterialSuggestionIndex >= 0 ? activeMaterialSuggestionIndex : 0
                                  ];
                                if (picked) {
                                  handleSelectItem(i, picked);
                                  setTimeout(() => focusQty(i), 0);
                                }
                                return;
                              }
                              focusQty(i);
                            }
                          }}
                        onBlur={() => {
                          setTimeout(() => {
                            setOpenMaterialIndex(null);
                            setActiveMaterialSuggestionIndex(-1);
                          }, 100);
                        }}
                        />
                        {openMaterialIndex === i && hasQuery && materialOptions.length > 0 && materialRects[i] ? (
                          <PortalDropdown
                            className="autocomplete-dropdown"
                            style={{
                              position: "absolute",
                              top: materialRects[i].bottom + window.scrollY + 6,
                              left: materialRects[i].left + window.scrollX,
                              width: materialRects[i].width,
                            }}
                          >
                            {materialOptions.map((item, optionIndex) => (
                              <button
                                key={item.id || item.code || item.name}
                                type="button"
                                ref={(el) => {
                                  materialSuggestionRefs.current[optionIndex] = el;
                                }}
                                style={{
                                  width: "100%",
                                  textAlign: "left",
                                  padding: "8px 10px",
                                  border: "none",
                                  background: optionIndex === activeMaterialSuggestionIndex ? "#eff6ff" : "transparent",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={() => setActiveMaterialSuggestionIndex(optionIndex)}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleSelectItem(i, item);
                                }}
                              >
                                <div style={{ fontWeight: 600 }}>{item.name || item.code}</div>
                                <div style={{ fontSize: "0.8rem", color: "#475569" }}>
                                  {item.code ? `${item.code}` : ""} {item.sellingPrice ? `| ₹${Number(item.sellingPrice).toFixed(2)}` : ""}
                                </div>
                              </button>
                            ))}
                          </PortalDropdown>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <input
                        className="input-field numeric"
                        type="number"
                        step="any"
                        placeholder="0"
                        value={row.qty}
                        onChange={(e)=>handleChange(i,'qty',e.target.value)}
                        data-qty-row={i}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            focusRate(i);
                          }
                        }}
                      />
                    </td>
                    <td>
                      <input
                        className="input-field numeric"
                        type="number"
                        step="any"
                        placeholder="0.00"
                        value={row.rate}
                        onChange={(e)=>handleChange(i,'rate',e.target.value)}
                        data-rate-row={i}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            focusNextRowMaterial(i);
                          }
                        }}
                      />
                    </td>
                    <td>
                      <input className="input-field numeric calculated" type="number" readOnly value={row.amount} />
                    </td>
                    <td className="actions">
                      <button type="button" className="btn-remove" title="Remove" onClick={()=>removeRow(i)}>x</button>
                    </td>
                  </tr>
                   );
                 })}
               </tbody>
               <tfoot>
                 <tr className="total-row">
                  <td className="total-label">Total</td>
                  <td className="text-right quantity">{totals.totalQty}</td>
                  <td></td>
                  <td className="text-right amount">{totals.totalAmount.toFixed ? totals.totalAmount.toFixed(2) : totals.totalAmount}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={addRow}>+ Add Row</button>
            {showClosingStockOption ? (
              <button type="button" className="btn-secondary" onClick={openClosingStockModal}>
                Closing Stock
              </button>
            ) : null}
            {editingEntry ? (
              <>
                <button type="button" className="btn-secondary" onClick={resetForm}>Cancel Edit</button>
                <button type="submit" className="btn-primary">Update Sale</button>
              </>
            ) : (
              <button type="submit" className="btn-primary">Save Sale</button>
            )}
          </div>
        </form>
      </div>

      {closingStockModal.open ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={closeClosingStockModal}>
          <div
            className="modal-card"
            style={{ width: "min(900px, 100%)", maxHeight: "85vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">{activeCategory} Closing Stock</h3>
              <button type="button" className="btn-remove" onClick={closeClosingStockModal} style={{ width: 28, height: 28, padding: 0 }}>
                x
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 320px) auto", gap: 10, alignItems: "end", marginBottom: 12 }}>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Inward Date</label>
                  <select
                    className="input-field"
                    value={closingStockSourceDate}
                    onChange={(e) => {
                      setClosingStockSourceDate(e.target.value);
                      setClosingStockSourceError("");
                    }}
                    disabled={closingStockModal.loading || closingStockAvailableDates.length === 0}
                  >
                    {closingStockAvailableDates.length === 0 ? (
                      <option value="">No inward dates found</option>
                    ) : null}
                    {closingStockAvailableDates.map((d) => (
                      <option key={d} value={d}>
                        {formatDisplayDate(d)}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button type="button" className="btn-secondary" onClick={openClosingStockModal} disabled={closingStockModal.loading}>
                    {closingStockModal.loading ? "Loading..." : "Refresh Inward Data"}
                  </button>
                </div>
              </div>

              {closingStockSourceError ? (
                <div style={{ marginBottom: 10, color: "#b91c1c", fontSize: "0.85rem" }}>{closingStockSourceError}</div>
              ) : null}

              <div style={{ marginBottom: 10, fontSize: "0.85rem", color: "#475569" }}>
                Materials are loaded from {activeCategory} Material Inward for the selected date. Update only `Qty` and `Rate`.
              </div>

              <div className="entries-table-container">
                <table className="entries-table" style={{ minWidth: 760 }}>
                  <thead>
                    <tr>
                      <th>Material</th>
                      <th className="text-right">Closing Stock</th>
                      <th className="text-right">Qty</th>
                      <th className="text-right">Rate</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closingStockModal.loading ? (
                      <tr>
                        <td colSpan={5} className="empty-state" style={{ padding: 12 }}>Loading inward items...</td>
                      </tr>
                    ) : closingStockSourceRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="empty-state" style={{ padding: 12 }}>
                          {closingStockSourceDate ? "No inward items for selected date" : "Select an inward date"}
                        </td>
                      </tr>
                    ) : (
                      closingStockSourceRows.map((row, idx) => (
                        <tr key={`${row.material}-${idx}`}>
                          <td>{row.material}</td>
                          <td className="text-right">{Number(row.closingStock || 0).toFixed(3)}</td>
                          <td>
                            <input
                              className="input-field numeric"
                              type="number"
                              step="any"
                              placeholder="0"
                              value={row.qty}
                              onChange={(e) => handleClosingStockModalRowChange(idx, "qty", e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              className="input-field numeric"
                              type="number"
                              step="any"
                              placeholder="0.00"
                              value={row.rate}
                              onChange={(e) => handleClosingStockModalRowChange(idx, "rate", e.target.value)}
                            />
                          </td>
                          <td className="text-right">{Number(row.amount || 0).toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeClosingStockModal}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={applyClosingStockRowsToForm}
                disabled={closingStockModal.loading || closingStockSourceRows.length === 0}
              >
                Apply to Sales
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showCustomerModal ? (
        <div className="modal-backdrop">
          <div className="modal-card">
            <div className="modal-header">
              <h3 className="modal-title">Add Customer</h3>
              <button type="button" className="btn-remove" onClick={() => setShowCustomerModal(false)} style={{ width: 28, height: 28, padding: 0 }}>
                x
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="field">
                  <label>Name</label>
                  <input
                    className="input-field"
                    type="text"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Customer name"
                    autoFocus
                  />
                </div>
                <div className="field">
                  <label>Phone</label>
                  <input
                    className="input-field"
                    type="text"
                    inputMode="numeric"
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="10-digit mobile"
                  />
                </div>
                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <label>Address</label>
                  <textarea
                    className="input-field"
                    rows={2}
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer((prev) => ({ ...prev, address: e.target.value }))}
                    placeholder="Address"
                  />
                </div>
              </div>
              {newCustomerError ? (
                <div style={{ marginTop: "8px", color: "#b91c1c", fontSize: "0.875rem" }}>
                  {newCustomerError}
                </div>
              ) : null}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowCustomerModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={handleSaveCustomer}>
                Save Customer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="entries-card">
        <div className="card-header" style={{borderBottom:'none'}}>
          <h2 className="card-title">Saved Sales</h2>
          <span className="entry-count">
            {savedSalesEntries.length}
            {showSavedCustomerFilter && savedCustomerFilter ? ` / ${dateFilteredVisibleEntries.length}` : ""} entries
          </span>
        </div>
        {showSavedCustomerFilter ? (
          <div style={{ padding: "12px 16px 0" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ minWidth: 180 }}>
                <label style={{ display: "block", fontSize: "12px", color: "#64748b", marginBottom: 6 }}>
                  From Date
                </label>
                <input
                  type="date"
                  className="input-field"
                  value={savedDateFromFilter}
                  onChange={(e) => setSavedDateFromFilter(e.target.value)}
                />
              </div>
              <div style={{ minWidth: 180 }}>
                <label style={{ display: "block", fontSize: "12px", color: "#64748b", marginBottom: 6 }}>
                  To Date
                </label>
                <input
                  type="date"
                  className="input-field"
                  value={savedDateToFilter}
                  onChange={(e) => setSavedDateToFilter(e.target.value)}
                />
              </div>
              <div style={{ minWidth: 260, flex: "1 1 320px" }}>
                <label style={{ display: "block", fontSize: "12px", color: "#64748b", marginBottom: 6 }}>
                  Customer Filter
                </label>
                <select
                  className="select-field"
                  value={savedCustomerFilter}
                  onChange={(e) => setSavedCustomerFilter(e.target.value)}
                >
                  <option value="">All Customers</option>
                  {customerFilterOptions.map((customer) => (
                    <option key={customer} value={customer}>
                    {customer}
                  </option>
                ))}
                </select>
              </div>
              {(savedDateFromFilter || savedDateToFilter || savedCustomerFilter) ? (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setSavedDateFromFilter("");
                    setSavedDateToFilter("");
                    setSavedCustomerFilter("");
                  }}
                  style={{ padding: "0.55rem 0.9rem" }}
                >
                  Clear Filters
                </button>
              ) : null}
              {savedCustomerFilter && selectedCustomerTotals ? (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "stretch", flex: "2 1 420px" }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => openCustomerDetailsModal("qty")}
                    style={{ padding: "8px 10px", minWidth: 120, textAlign: "left" }}
                    title="Open date-wise quantity details"
                  >
                    <div style={{ fontSize: "0.72rem", color: "#64748b" }}>Total Qty</div>
                    <div style={{ fontWeight: 800 }}>{Number(selectedCustomerTotals.qty || 0).toFixed(2)}</div>
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => openCustomerDetailsModal("amount")}
                    style={{ padding: "8px 10px", minWidth: 140, textAlign: "left" }}
                    title="Open date-wise amount details"
                  >
                    <div style={{ fontSize: "0.72rem", color: "#64748b" }}>Total Amount</div>
                    <div style={{ fontWeight: 800 }}>{Number(selectedCustomerTotals.amount || 0).toFixed(2)}</div>
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => openCustomerDetailsModal("paid")}
                    style={{ padding: "8px 10px", minWidth: 140, textAlign: "left" }}
                    title="Open date-wise paid details"
                  >
                    <div style={{ fontSize: "0.72rem", color: "#64748b" }}>Total Paid</div>
                    <div style={{ fontWeight: 800, color: "#16a34a" }}>
                      {Number(selectedCustomerTotals.paid || 0).toFixed(2)}
                    </div>
                  </button>
                  <div
                    style={{
                      padding: "8px 10px",
                      minWidth: 140,
                      textAlign: "left",
                      borderRadius: 10,
                      border: "1px solid #cbd5e1",
                      background: "#f8fafc",
                      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                    }}
                    title="Remaining balance after payments and adjustments"
                  >
                    <div style={{ fontSize: "0.72rem", color: "#64748b" }}>Balance</div>
                    <div
                      style={{
                        fontWeight: 800,
                        color: Number(selectedCustomerTotals.balance || 0) > 0 ? "#b91c1c" : "#16a34a",
                      }}
                    >
                      {Number(selectedCustomerTotals.balance || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        <>
          <div className="entries-table-container">
            <table
              className="entries-table"
              style={showSavedPaymentTypeColumn || showSavedPrintColumn || showSavedWhatsAppColumn ? { minWidth: 1300 } : undefined}
            >
              <thead>
                <tr>
                  <th style={{width:40}}></th>
                  <th>Category</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Amount</th>
                  {showSavedPaymentTypeColumn ? <th>Payment Type</th> : null}
                  {showSavedPrintColumn ? <th>Print</th> : null}
                  {showSavedWhatsAppColumn ? <th>WhatsApp</th> : null}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {savedSalesEntries.length === 0 ? (
                  <tr>
                    <td
                      colSpan={
                        7 +
                        (showSavedPaymentTypeColumn ? 1 : 0) +
                        (showSavedPrintColumn ? 1 : 0) +
                        (showSavedWhatsAppColumn ? 1 : 0)
                      }
                      className="empty-state"
                    >
                      {savedCustomerFilter ? "No sales for selected customer" : "No sales yet"}
                    </td>
                  </tr>
                ) : pagedSavedSalesEntries.map((en) => {
                const isOpen = !!expanded[en.id];
                const editReq = requestByEntry[en.id]?.edit;
                const delReq = requestByEntry[en.id]?.delete;
                const editKey = requestKey(en.id, "edit");
                const delKey = requestKey(en.id, "delete");
                const editStatus = String(editReq?.status || "");
                const delStatus = String(delReq?.status || "");
                const editBusy = !!saleRequesting[editKey];
                const delBusy = !!saleRequesting[delKey];
                const deleting = !!entryDeleting[en.id];
                return (
                  <React.Fragment key={en.id}>
                    <tr>
                      <td><button type="button" className="expander" onClick={()=> setExpanded((ex)=> ({...ex, [en.id]: !isOpen}))}>{isOpen ? 'v' : '>'}</button></td>
                      <td>{en.category}</td>
                      <td>{en.customer || 'Walk-in'}</td>
                      <td>{formatDisplayDate(en.saleDate)}</td>
                      <td className="text-right">{en.totalQty}</td>
                      <td className="text-right">{Number(en.total).toFixed(2)}</td>
                      {showSavedPaymentTypeColumn ? (
                        <td>
                          {canUpdatePaymentType ? (
                            <select
                              className="input-field compact"
                              value={normalizeSalesPaymentType(en.paymentType)}
                              disabled={!!paymentTypeUpdating[en.id]}
                              onChange={(e) =>
                                setSavedEntryPaymentType(en.id, en.paymentType, e.target.value)
                              }
                            >
                              {SALES_PAYMENT_TYPES.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span
                              style={{
                                fontWeight: 600,
                                color:
                                  normalizeSalesPaymentType(en.paymentType) === "Credit"
                                    ? "#b91c1c"
                                    : normalizeSalesPaymentType(en.paymentType) === "UPI"
                                      ? "#1d4ed8"
                                      : "#334155",
                              }}
                            >
                              {normalizeSalesPaymentType(en.paymentType)}
                            </span>
                          )}
                        </td>
                      ) : null}
                      {showSavedPrintColumn ? (
                        <td>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => handlePrintEntry(en)}
                            style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                          >
                            Print
                          </button>
                        </td>
                      ) : null}
                      {showSavedWhatsAppColumn ? (
                        <td>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => handleSendWhatsApp(en)}
                            style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                          >
                            WhatsApp
                          </button>
                        </td>
                      ) : null}
                      <td className="text-right">
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap" }}>
                          {isAdmin ? (
                            <>
                              <button type="button" className="btn-secondary" onClick={()=> startEditEntry(en)} disabled={deleting} style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}>Edit</button>
                              <button type="button" className="btn-remove" onClick={()=> handleDeleteEntry(en)} disabled={deleting} style={{ width: 32, height: 32, padding: 0 }}>x</button>
                            </>
                          ) : (
                            <>
                              {editStatus === "approved" ? (
                                <button type="button" className="btn-secondary" onClick={()=> startEditEntry(en)} disabled={deleting} style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}>Edit</button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={()=> requestEntryAction(en, "edit")}
                                  disabled={editBusy || editStatus === "pending"}
                                  style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                  title={editStatus === "pending" ? "Edit request pending" : "Request edit"}
                                >
                                  {editStatus === "pending" ? "Edit Pending" : "Request Edit"}
                                </button>
                              )}

                              {delStatus === "approved" ? (
                                <button type="button" className="btn-remove" onClick={()=> handleDeleteEntry(en)} disabled={deleting} style={{ width: 32, height: 32, padding: 0 }}>x</button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={()=> requestEntryAction(en, "delete")}
                                  disabled={delBusy || delStatus === "pending"}
                                  style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                                  title={delStatus === "pending" ? "Delete request pending" : "Request delete"}
                                >
                                  {delStatus === "pending" ? "Delete Pending" : "Request Delete"}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td></td>
                        <td
                          colSpan={
                            6 +
                            (showSavedPaymentTypeColumn ? 1 : 0) +
                            (showSavedPrintColumn ? 1 : 0) +
                            (showSavedWhatsAppColumn ? 1 : 0)
                          }
                        >
                          <div className="items-section">
                            <div className="section-title">Items</div>
                            <table className="modern-table" style={{margin:'8px 0'}}>
                              <thead>
                                <tr>
                                  <th>Material</th>
                                  {en.category === "Stall" || en.category === "Market" ? (
                                    <th className="text-right">Closing Stock</th>
                                  ) : null}
                                  <th className="text-right">Qty</th>
                                  <th className="text-right">Rate</th>
                                  <th className="text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {en.items.map((it, idx) => (
                                  <tr key={idx}>
                                    <td>{it.material}</td>
                                    {en.category === "Stall" || en.category === "Market" ? (
                                      <td className="text-right">{Number(it.closingStock || 0).toFixed(3)}</td>
                                    ) : null}
                                    <td className="text-right">{it.qty}</td>
                                    <td className="text-right">{Number(it.rate).toFixed(2)}</td>
                                    <td className="text-right">{Number(it.amount).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div className="payments-section" style={{marginTop:'8px'}}>
                            <div className="payments-header">Payments</div>
                            <table className="entries-table" style={{marginTop:'6px'}}>
                              <thead>
                                <tr>
                                  <th>Mode</th>
                                  <th>Date</th>
                                  <th className="text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(en.payments || []).length === 0 ? (
                                  <tr><td colSpan={3} className="empty-state" style={{padding:'8px'}}>No payments yet</td></tr>
                                ) : (
                                  (en.payments || []).map((p) => (
                                    <tr key={p.id}>
                                      <td>{p.mode}</td>
                                      <td>{formatDisplayDate(p.date)}</td>
                                      <td className="text-right">{Number(p.amount).toFixed(2)}</td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
                })}
              </tbody>
            </table>
          </div>
          <div
            style={{
              padding: "0 1.5rem 1.25rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontSize: "0.82rem", color: "#475569" }}>
              {`Showing ${savedSalesPageStart}-${savedSalesPageEnd} of ${savedSalesEntries.length} entries`}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setSavedSalesPage((prev) => Math.max(1, prev - 1))}
                disabled={savedSalesPage <= 1}
                style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
              >
                Prev
              </button>
              <span className="entry-count">{`Page ${savedSalesPage} / ${savedSalesTotalPages}`}</span>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setSavedSalesPage((prev) => Math.min(savedSalesTotalPages, prev + 1))}
                disabled={savedSalesPage >= savedSalesTotalPages}
                style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      </div>

      {showSavedCustomerFilter && customerDetailsModal.open && savedCustomerFilter ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={closeCustomerDetailsModal}>
          <div
            className="modal-card"
            style={{ width: "min(860px, 100%)", maxHeight: "85vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-title">Customer Date-wise Details - {savedCustomerFilter}</div>
            <div className="modal-body">
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center",
                  marginBottom: 12,
                  fontSize: "0.85rem",
                }}
              >
                <span style={{ color: "#475569" }}>
                  Opened from:{" "}
                  <strong>
                    {customerDetailsModal.metric === "qty"
                      ? "Total Qty"
                      : customerDetailsModal.metric === "amount"
                        ? "Total Amount"
                        : "Total Paid"}
                  </strong>
                </span>
                {customerPaidOnlyModal ? (
                  <span style={{ marginLeft: "auto", fontWeight: 700, color: "#16a34a" }}>
                    Paid: {Number(selectedCustomerTotals?.paid || 0).toFixed(2)}
                  </span>
                ) : (
                  <>
                    <span style={{ marginLeft: "auto", fontWeight: 700 }}>
                      Qty: {Number(selectedCustomerTotals?.qty || 0).toFixed(2)}
                    </span>
                    <span style={{ fontWeight: 700 }}>
                      Amount: {Number(selectedCustomerTotals?.amount || 0).toFixed(2)}
                    </span>
                    <span style={{ fontWeight: 700, color: "#16a34a" }}>
                      Paid: {Number(selectedCustomerTotals?.paid || 0).toFixed(2)}
                    </span>
                    <span style={{ fontWeight: 700, color: "#b91c1c" }}>
                      Balance: {Number(selectedCustomerTotals?.balance || 0).toFixed(2)}
                    </span>
                  </>
                )}
              </div>

              {customerPaidOnlyModal ? (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginBottom: 12,
                    padding: "10px",
                    border: "1px solid #dbeafe",
                    background: "#f8fbff",
                    borderRadius: 10,
                  }}
                >
                  <input
                    type="date"
                    className="input-field compact"
                    max={new Date().toISOString().slice(0, 10)}
                    value={customerPaidForm.date}
                    onChange={(e) => setCustomerPaidForm((prev) => ({ ...prev, date: e.target.value }))}
                    style={{ width: 160 }}
                    disabled={customerPaidSaving}
                  />
                  <input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    className="input-field compact numeric"
                    placeholder="Paid amount"
                    value={customerPaidForm.amount}
                    onChange={(e) => setCustomerPaidForm((prev) => ({ ...prev, amount: e.target.value }))}
                    style={{ width: 180 }}
                    disabled={customerPaidSaving}
                  />
                  <button
                    type="button"
                    className="btn-add-payment"
                    onClick={handleAddCustomerPaidFromModal}
                    disabled={customerPaidSaving}
                  >
                    {customerPaidSaving ? "Saving..." : "Add Paid Amount"}
                  </button>
                </div>
              ) : null}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  marginBottom: 10,
                }}
              >
                <div style={{ fontSize: "0.85rem", color: "#475569" }}>
                  Showing {customerDetailsPageStart}-{customerDetailsPageEnd} of {customerDetailsRowsForModal.length}
                </div>
                {customerDetailsTotalPages > 1 ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setCustomerDetailsPage((p) => Math.max(1, p - 1))}
                      disabled={effectiveCustomerDetailsPage <= 1}
                      style={{ padding: "0.3rem 0.65rem", fontSize: "0.75rem" }}
                    >
                      Prev
                    </button>
                    <span style={{ fontSize: "0.85rem", fontWeight: 700 }}>
                      Page {effectiveCustomerDetailsPage} / {customerDetailsTotalPages}
                    </span>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setCustomerDetailsPage((p) => Math.min(customerDetailsTotalPages, p + 1))}
                      disabled={effectiveCustomerDetailsPage >= customerDetailsTotalPages}
                      style={{ padding: "0.3rem 0.65rem", fontSize: "0.75rem" }}
                    >
                      Next
                    </button>
                  </div>
                ) : null}
              </div>

              <div className="entries-table-container">
                <table className="entries-table" style={{ minWidth: customerPaidOnlyModal ? 760 : 720 }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      {customerPaidOnlyModal ? <th>Mode(s)</th> : null}
                      {customerPaidOnlyModal ? <th className="text-right">Payment Rows</th> : null}
                      {!customerPaidOnlyModal ? <th className="text-right">Entries</th> : null}
                      {!customerPaidOnlyModal ? (
                        <th
                          className="text-right"
                          style={
                            customerDetailsModal.metric === "qty"
                              ? { background: "#1d4ed8", color: "#fff", fontWeight: 800 }
                              : undefined
                          }
                        >
                          Total Qty
                        </th>
                      ) : null}
                      {!customerPaidOnlyModal ? (
                        <th
                          className="text-right"
                          style={
                            customerDetailsModal.metric === "amount"
                              ? { background: "#7c3aed", color: "#fff", fontWeight: 800 }
                              : undefined
                          }
                        >
                          Total Amount
                        </th>
                      ) : null}
                      {!customerAmountModal ? (
                        <th
                          className="text-right"
                          style={
                            customerDetailsModal.metric === "paid"
                              ? { background: "#059669", color: "#fff", fontWeight: 800 }
                              : undefined
                          }
                        >
                          Total Paid
                        </th>
                      ) : null}
                      {customerPaidOnlyModal ? <th style={{ width: 260 }}>Actions</th> : null}
                      {!customerPaidOnlyModal ? <th className="text-right">Balance</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {customerDetailsPagedRows.length === 0 ? (
                      <tr>
                        <td colSpan={customerDetailsColSpan} className="empty-state" style={{ padding: "12px" }}>
                          No data for selected customer
                        </td>
                      </tr>
                    ) : customerPaidOnlyModal ? (
                      customerDetailsPagedRows.map((row, rowIndex) => {
                        const singlePayment = row.singlePayment;
                        const isExpanded = !!expandedCustomerPaidDates[row.date];
                        const singlePaymentDeleting = singlePayment ? !!customerPaidDeleting[singlePayment.paymentId] : false;
                        const isSingleEditing =
                          !!singlePayment && customerPaidEdit.paymentId === singlePayment.paymentId;

                        return (
                          <React.Fragment key={row.date || `paid-${rowIndex}`}>
                            <tr>
                              <td>
                                {isSingleEditing ? (
                                  <input
                                    type="date"
                                    className="input-field compact"
                                    max={new Date().toISOString().slice(0, 10)}
                                    value={customerPaidEdit.date}
                                    onChange={(e) => setCustomerPaidEdit((prev) => ({ ...prev, date: e.target.value }))}
                                    style={{ width: 160 }}
                                    disabled={customerPaidUpdating}
                                  />
                                ) : (
                                  formatDisplayDate(row.date)
                                )}
                              </td>
                              <td>{singlePayment ? singlePayment.mode || "Cash" : row.modesText}</td>
                              <td className="text-right">{Number(row.paymentCount || 0)}</td>
                              <td
                                className="text-right"
                                style={{ background: "#ecfdf5", fontWeight: 700, color: "#16a34a" }}
                              >
                                {isSingleEditing ? (
                                  <input
                                    type="number"
                                    inputMode="decimal"
                                    step="any"
                                    className="input-field compact numeric"
                                    value={customerPaidEdit.amount}
                                    onChange={(e) => setCustomerPaidEdit((prev) => ({ ...prev, amount: e.target.value }))}
                                    style={{ width: 140, marginLeft: "auto" }}
                                    disabled={customerPaidUpdating}
                                  />
                                ) : (
                                  Number(row.totalPaid || 0).toFixed(2)
                                )}
                              </td>
                              <td>
                                {isSingleEditing ? (
                                  <div style={{ display: "flex", gap: 6 }}>
                                    <button
                                      type="button"
                                      className="btn-add-payment"
                                      onClick={handleSaveEditCustomerPaid}
                                      disabled={customerPaidUpdating}
                                      style={{ padding: "0.3rem 0.7rem", fontSize: "0.75rem" }}
                                    >
                                      {customerPaidUpdating ? "Saving..." : "Save"}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-secondary"
                                      onClick={handleCancelEditCustomerPaid}
                                      disabled={customerPaidUpdating}
                                      style={{ padding: "0.3rem 0.7rem", fontSize: "0.75rem" }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : singlePayment ? (
                                  <div style={{ display: "flex", gap: 6 }}>
                                    <button
                                      type="button"
                                      className="btn-secondary"
                                      onClick={() => handleStartEditCustomerPaid(singlePayment)}
                                      disabled={customerPaidUpdating || singlePaymentDeleting}
                                      style={{ padding: "0.3rem 0.7rem", fontSize: "0.75rem" }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-secondary"
                                      onClick={() => handleDeleteCustomerPaid(singlePayment)}
                                      disabled={customerPaidUpdating || singlePaymentDeleting}
                                      style={{
                                        padding: "0.3rem 0.7rem",
                                        fontSize: "0.75rem",
                                        borderColor: "#fecaca",
                                        color: "#b91c1c",
                                        background: "#fff5f5",
                                      }}
                                    >
                                      {singlePaymentDeleting ? "Deleting..." : "Delete"}
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() =>
                                      setExpandedCustomerPaidDates((prev) => ({ ...prev, [row.date]: !prev[row.date] }))
                                    }
                                    style={{ padding: "0.3rem 0.7rem", fontSize: "0.75rem" }}
                                  >
                                    {isExpanded ? "Hide Splits" : `View Splits (${row.paymentCount})`}
                                  </button>
                                )}
                              </td>
                            </tr>
                            {!singlePayment && isExpanded ? (
                              <tr>
                                <td colSpan={customerDetailsColSpan} style={{ background: "#f8fafc", padding: 0 }}>
                                  <div style={{ padding: "12px 14px" }}>
                                    <div
                                      style={{
                                        fontSize: "0.8rem",
                                        fontWeight: 700,
                                        color: "#475569",
                                        marginBottom: 8,
                                      }}
                                    >
                                      Registered on {formatDisplayDate(row.date)} and allocated across previous sales
                                    </div>
                                    <table className="entries-table" style={{ minWidth: 640, background: "#fff" }}>
                                      <thead>
                                        <tr>
                                          <th>Sale Date</th>
                                          <th>Payment Date</th>
                                          <th>Mode</th>
                                          <th className="text-right">Amount</th>
                                          <th style={{ width: 230 }}>Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {row.items.map((paymentRow) => {
                                          const isEditing = customerPaidEdit.paymentId === paymentRow.paymentId;
                                          return (
                                            <tr key={paymentRow.paymentId}>
                                              <td>{formatDisplayDate(paymentRow.saleDate || "")}</td>
                                              <td>
                                                {isEditing ? (
                                                  <input
                                                    type="date"
                                                    className="input-field compact"
                                                    max={new Date().toISOString().slice(0, 10)}
                                                    value={customerPaidEdit.date}
                                                    onChange={(e) =>
                                                      setCustomerPaidEdit((prev) => ({ ...prev, date: e.target.value }))
                                                    }
                                                    style={{ width: 160 }}
                                                    disabled={customerPaidUpdating}
                                                  />
                                                ) : (
                                                  formatDisplayDate(paymentRow.date || "")
                                                )}
                                              </td>
                                              <td>{paymentRow.mode || "Cash"}</td>
                                              <td className="text-right" style={{ color: "#16a34a", fontWeight: 700 }}>
                                                {isEditing ? (
                                                  <input
                                                    type="number"
                                                    inputMode="decimal"
                                                    step="any"
                                                    className="input-field compact numeric"
                                                    value={customerPaidEdit.amount}
                                                    onChange={(e) =>
                                                      setCustomerPaidEdit((prev) => ({ ...prev, amount: e.target.value }))
                                                    }
                                                    style={{ width: 140, marginLeft: "auto" }}
                                                    disabled={customerPaidUpdating}
                                                  />
                                                ) : (
                                                  Number(paymentRow.totalPaid || 0).toFixed(2)
                                                )}
                                              </td>
                                              <td>
                                                {isEditing ? (
                                                  <div style={{ display: "flex", gap: 6 }}>
                                                    <button
                                                      type="button"
                                                      className="btn-add-payment"
                                                      onClick={handleSaveEditCustomerPaid}
                                                      disabled={customerPaidUpdating}
                                                      style={{ padding: "0.3rem 0.7rem", fontSize: "0.75rem" }}
                                                    >
                                                      {customerPaidUpdating ? "Saving..." : "Save"}
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className="btn-secondary"
                                                      onClick={handleCancelEditCustomerPaid}
                                                      disabled={customerPaidUpdating}
                                                      style={{ padding: "0.3rem 0.7rem", fontSize: "0.75rem" }}
                                                    >
                                                      Cancel
                                                    </button>
                                                  </div>
                                                ) : (
                                                  <div style={{ display: "flex", gap: 6 }}>
                                                    <button
                                                      type="button"
                                                      className="btn-secondary"
                                                      onClick={() => handleStartEditCustomerPaid(paymentRow)}
                                                      disabled={
                                                        customerPaidUpdating || !!customerPaidDeleting[paymentRow.paymentId]
                                                      }
                                                      style={{ padding: "0.3rem 0.7rem", fontSize: "0.75rem" }}
                                                    >
                                                      Edit
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className="btn-secondary"
                                                      onClick={() => handleDeleteCustomerPaid(paymentRow)}
                                                      disabled={
                                                        customerPaidUpdating || !!customerPaidDeleting[paymentRow.paymentId]
                                                      }
                                                      style={{
                                                        padding: "0.3rem 0.7rem",
                                                        fontSize: "0.75rem",
                                                        borderColor: "#fecaca",
                                                        color: "#b91c1c",
                                                        background: "#fff5f5",
                                                      }}
                                                    >
                                                      {customerPaidDeleting[paymentRow.paymentId] ? "Deleting..." : "Delete"}
                                                    </button>
                                                  </div>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })
                    ) : (
                      customerDetailsPagedRows.map((row, rowIndex) => (
                        <tr key={`${row.date}-${rowIndex}`}>
                          <td>{formatDisplayDate(row.date)}</td>
                          <td className="text-right">{row.entryCount}</td>
                          <td
                            className="text-right"
                            style={
                              customerDetailsModal.metric === "qty" ? { background: "#eff6ff", fontWeight: 700 } : undefined
                            }
                            >
                              {Number(row.totalQty || 0).toFixed(2)}
                            </td>
                          <td
                            className="text-right"
                            style={
                              customerDetailsModal.metric === "amount"
                                ? { background: "#f5f3ff", fontWeight: 700 }
                                : undefined
                            }
                          >
                            {Number(row.totalAmount || 0).toFixed(2)}
                          </td>
                          {!customerAmountModal ? (
                            <td
                              className="text-right"
                              style={
                                customerDetailsModal.metric === "paid"
                                  ? { background: "#ecfdf5", fontWeight: 700 }
                                  : { color: "#16a34a" }
                              }
                            >
                              {Number(row.totalPaid || 0).toFixed(2)}
                            </td>
                          ) : null}
                          <td
                            className="text-right"
                            style={{ color: Number(row.balance || 0) > 0 ? "#b91c1c" : "#16a34a" }}
                          >
                            {Number(row.balance || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeCustomerDetailsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
